import React, { useCallback } from "react";
import * as R from "ramda";
import { Button } from "antd";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";

import api from "../../utils/api";
import { sendMessage } from "../../store/actions/chat";
import { ACCEPT_LANGUAGE } from "../../locales/acceptedLanguages";

import styled from "styled-components/macro";

const CalendarMessageWrapper = styled.div`
  text-align: center;
  margin: 16px 0;
`;

const statuses = {
  LOADING: "LOADING",
  ERROR: "ERROR",
  SUCCESS: "SUCCESS"
};

const WrapperDateSelected = styled.div`
  display: flex;
  flex-direction: column;
`;

function CalendarMessage({
  message = {},
  idCompany,
  idOpportunity,
  idCandidate
}) {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const locale = ACCEPT_LANGUAGE[i18n.language] || ACCEPT_LANGUAGE.DEFAULT;
  moment.locale(locale);

  const idStep = R.path(["widgetMetadata", "step"], message);
  const [calendar, setCalendar] = React.useState({
    status: statuses.LOADING
  });

  const fetchCalendar = useCallback(async () => {
    try {
      const calendarData = await api.chat.fetchCalendarData(
        idCompany,
        idOpportunity,
        idCandidate,
        idStep
      );

      setCalendar({
        status: statuses.SUCCESS,
        dateSelected: calendarData.dateSelected
      });
    } catch (error) {
      const noSelectedCalendarError =
        error && error.match("no calendar marked");
      setCalendar({
        status: statuses.ERROR,
        error: t(
          `opportunity:process.candidateDetails.${
            noSelectedCalendarError
              ? "errorNoSelectedCalendar"
              : "errorLoadingCalendarData"
          }`
        )
      });
    }
  }, [idCandidate, idCompany, idOpportunity, idStep, t]);
  React.useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  const displayText = R.cond([
    [
      R.propEq("status", statuses.LOADING),
      R.always(t("opportunity:process.candidateDetails.calendarDataLoading"))
    ],
    [R.propEq("status", statuses.ERROR), R.prop("error")],
    [
      R.prop("dateSelected"),
      ({ dateSelected }) => (
        <WrapperDateSelected>
          {t("opportunity:process.candidateDetails.calendarDateSelected")}
          <div style={{ marginTop: 8 }}>
            <b>{moment(dateSelected).format("LLLL")}</b>
            <Button
              size="small"
              type="tertiary"
              style={{ marginLeft: 16 }}
              onClick={() =>
                dispatch(
                  sendMessage(
                    t(
                      "opportunity:process.candidateDetails.requestChangeDateMessage"
                    ),
                    {
                      company: idCompany,
                      opportunity: idOpportunity,
                      candidate: idCandidate
                    }
                  )
                )
              }
            >
              {t("opportunity:process.candidateDetails.requestChange")}
            </Button>
          </div>
        </WrapperDateSelected>
      )
    ],
    [
      R.T,
      R.always(
        t("opportunity:process.candidateDetails.calendarDateNotSelected")
      )
    ]
  ])(calendar);

  return <CalendarMessageWrapper>{displayText}</CalendarMessageWrapper>;
}

export default CalendarMessage;
